import { useEffect, useRef, useState } from "react";

const TARGET_VOLUME = 0.45;

export default function MusicToggle({
  src = "/audio/jashn-e-bahaaraa-flute.mp3",
  play = false,
  labelOn = "Music on",
  labelOff = "Music",
}) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const resumeRef = useRef(false);

  const fadeTo = (vol, ms = 2000) => {
    const a = audioRef.current;
    if (!a) return;
    clearInterval(fadeRef.current);
    const steps = 24;
    const step = (vol - a.volume) / steps;
    let i = 0;
    fadeRef.current = setInterval(() => {
      i += 1;
      a.volume = Math.min(1, Math.max(0, a.volume + step));
      if (i >= steps) {
        a.volume = vol;
        clearInterval(fadeRef.current);
        if (vol === 0) a.pause();
      }
    }, ms / steps);
  };

  const begin = () => {
    const a = audioRef.current;
    if (!a || !a.paused) return;
    a.volume = 0;
    a.play()
      .then(() => {
        setPlaying(true);
        fadeTo(TARGET_VOLUME);
      })
      .catch(() => {
        /* autoplay blocked — the envelope tap or this button will start it */
      });
  };

  useEffect(() => {
    const a = new Audio(src);
    a.loop = true;
    // Don't compete with the page's own assets: buffer the track only
    // once the page has fully loaded (or the moment the guest taps play).
    a.preload = "none";
    audioRef.current = a;
    const warm = () => {
      a.preload = "auto";
      a.load();
    };
    if (document.readyState === "complete") {
      warm();
    } else {
      window.addEventListener("load", warm, { once: true });
    }
    // Try straight away, in case the browser already allows sound.
    begin();
    return () => {
      clearInterval(fadeRef.current);
      a.pause();
      a.src = "";
      audioRef.current = null;
    };
  }, [src]);

  // The page signals "the guest has opened the invitation".
  useEffect(() => {
    if (play) begin();
  }, [play]);

  // Phones keep a background tab's audio alive — halt the song the moment
  // the guest leaves the page, and pick it back up when they return.
  useEffect(() => {
    const halt = () => {
      const a = audioRef.current;
      if (!a || a.paused) return;
      resumeRef.current = true;
      clearInterval(fadeRef.current);
      a.pause();
    };
    const restore = () => {
      const a = audioRef.current;
      if (!a || !resumeRef.current || document.hidden) return;
      resumeRef.current = false;
      a.volume = TARGET_VOLUME;
      a.play().catch(() => setPlaying(false));
    };
    const onVisibility = () => (document.hidden ? halt() : restore());
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", halt);
    window.addEventListener("pageshow", restore);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", halt);
      window.removeEventListener("pageshow", restore);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing && !a.paused) {
      fadeTo(0, 900);
      setPlaying(false);
    } else {
      begin();
    }
  };

  return (
    <button
      type="button"
      className={`music-toggle${playing ? " on" : ""}`}
      onClick={toggle}
      aria-pressed={playing}
      aria-label={playing ? "Pause music" : "Play music"}
      data-testid="music-toggle"
    >
      <span className="music-bars" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <span className="music-label">{playing ? labelOn : labelOff}</span>
    </button>
  );
}
